import { api } from '@/lib/api/client';

export type ConflictStatus = 'open' | 'confirmed' | 'corrected' | 'suppressed';
export type ConflictClass = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
export type Severity = 'info' | 'low' | 'medium' | 'high';

export interface Evidence {
  side: 'a' | 'b';
  source: string;
  ref?: string;
  page?: number;
  excerpt?: string;
}

export interface ConflictEvent {
  id: string;
  action: string;
  fromStatus?: ConflictStatus;
  toStatus: ConflictStatus;
  profileId?: string;
  note?: string;
  createdAt: string;
}

export interface Conflict {
  id: string;
  class: ConflictClass;
  severity: Severity;
  status: ConflictStatus;
  title: string;
  entityType: string;
  entityId: string;
  field?: string;
  sourceA: string;
  valueA?: string;
  sourceB?: string;
  valueB?: string;
  proposedInterpretation: string;
  proposedValue?: string;
  raisedBy: string;
  assignedTo?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  resolvedValue?: string;
  resolutionNote?: string;
  evidence: Evidence[];
  events?: ConflictEvent[];
  createdAt: string;
}

/** Design doc §3.5. */
export const CLASS_LABEL: Record<ConflictClass, string> = {
  A: 'Expected lag',
  B: 'Meaningful divergence',
  C: 'Transcription',
  D: 'Parse ambiguity',
  E: 'Unresolved identity',
  F: 'Reconciliation',
  G: 'Stale period',
};

export function listConflicts(status: ConflictStatus | 'resolved' | 'all', signal?: AbortSignal): Promise<Conflict[]> {
  // sreg-api filters one status at a time; "resolved" merges the three resolutions client-side.
  const s = status === 'all' || status === 'resolved' ? undefined : status;
  return api
    .get<Conflict[]>('/conflicts', { status: s, limit: 200 }, signal)
    .then((rows) => (status === 'resolved' ? rows.filter((c) => c.status !== 'open') : rows));
}

export function getConflict(id: string, signal?: AbortSignal): Promise<Conflict> {
  return api.get<Conflict>(`/conflicts/${id}`, undefined, signal);
}

export function resolveConflict(
  id: string,
  body: { status: Exclude<ConflictStatus, 'open'>; resolvedValue?: string; note?: string },
): Promise<Conflict> {
  return api.post<Conflict>(`/conflicts/${id}/resolve`, body);
}

export function reopenConflict(id: string, note?: string): Promise<Conflict> {
  return api.post<Conflict>(`/conflicts/${id}/reopen`, { note });
}
